import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addPlanToCart,
  addToCart,
  checkoutCart,
  getCart,
  removeFromCart,
  reopenCart,
  updateCartQuantity,
  updatePlanAmount,
} from "./cart-api";
import type { CartSummary } from "./types";

const KEY = ["cart"] as const;

export const useCart = () => useQuery({ queryKey: KEY, queryFn: getCart, staleTime: 15_000 });

/**
 * Every cart call answers with the cart as it now stands, so each mutation writes
 * that straight into the cache instead of refetching.
 */
export function useCartMutations() {
  const qc = useQueryClient();
  const store = (cart: CartSummary) => qc.setQueryData(KEY, cart);
  return {
    add: useMutation({
      mutationFn: ({ productId, quantity, amount }: { productId: string; quantity?: number; amount?: number }) =>
        amount === undefined ? addToCart(productId, quantity) : addPlanToCart(productId, amount),
      onSuccess: store,
    }),
    setAmount: useMutation({
      mutationFn: ({ productId, amount }: { productId: string; amount: number }) =>
        updatePlanAmount(productId, amount),
      onSuccess: store,
    }),
    setQuantity: useMutation({
      mutationFn: ({ productId, quantity }: { productId: string; quantity: number }) =>
        updateCartQuantity(productId, quantity),
      onSuccess: store,
    }),
    remove: useMutation({ mutationFn: (productId: string) => removeFromCart(productId), onSuccess: store }),
    checkout: useMutation({ mutationFn: checkoutCart, onSuccess: store }),
    reopen: useMutation({ mutationFn: reopenCart, onSuccess: store }),
  };
}
